import { ModalContent } from "./ModalContent.js";

// 전역 ModalContent 노출
export { ModalContent };

/**
 * Modal Class
 * alert, info, error, success 타입의 모달을 생성하고 관리
 */
export class Modal {
    // Depth 레벨 정의 (높을수록 위에 표시)
    static DEPTH = {
        LOW: 10000,      // 기본 모달
        MEDIUM: 10050,   // 중요 모달
        HIGH: 10100,     // 매우 중요 모달
        CRITICAL: 10150, // 최우선 모달
    };

    constructor(options = {}) {
        this.type = options.type || 'alert'; // alert, info, error, success
        this.content = options.content || null; // ModalContent 인스턴스 또는 문자열
        this.message = options.message || '';
        this.title = options.title || '';
        this.subtitle = options.subtitle || '';
        this.onClose = options.onClose || null;
        this.onConfirm = options.onConfirm || null;
        this.autoClose = options.autoClose !== undefined ? options.autoClose : false;
        this.autoCloseDelay = options.autoCloseDelay || 3000;
        this.depth = options.depth || Modal.DEPTH.LOW; // depth 레벨
        
        this.modalElement = null;
        this.overlayElement = null;
        this.isOpen = false;
        this.autoCloseTimer = null;
    }

    /**
     * 모달 생성
     */
    create() {
        // 오버레이 생성
        this.overlayElement = document.createElement('div');
        this.overlayElement.className = 'no-modal-overlay';
        this.overlayElement.setAttribute('data-modal-overlay', '');
        this.overlayElement.style.zIndex = this.depth.toString();

        // 모달 컨테이너 생성
        this.modalElement = document.createElement('div');
        this.modalElement.className = `no-modal no-modal--${this.type}`;
        this.modalElement.setAttribute('role', 'dialog');
        this.modalElement.setAttribute('aria-modal', 'true');
        this.modalElement.setAttribute('aria-labelledby', 'modal-title');

        // 모달 헤더
        const header = document.createElement('div');
        header.className = 'no-modal__header';

        // 아이콘 및 제목
        const titleContainer = document.createElement('div');
        titleContainer.className = 'no-modal__title-container';

        // 타입별 아이콘
        const icon = this.getIcon();
        if (icon) {
            const iconElement = document.createElement('div');
            iconElement.className = 'no-modal__icon';
            iconElement.innerHTML = icon;
            titleContainer.appendChild(iconElement);
        }

        // 제목(+ 부제목) 그룹 — 부제목이 있으면 세로 배치
        const hasTitle = this.title || (this.content instanceof ModalContent && this.content.title);
        if (hasTitle || this.subtitle) {
            const titleGroup = document.createElement('div');
            titleGroup.className = 'no-modal__title-group';
            if (hasTitle) {
                const title = document.createElement('h3');
                title.id = 'modal-title';
                title.className = 'no-modal__title';
                title.textContent = this.title || (this.content instanceof ModalContent ? this.content.title : '');
                titleGroup.appendChild(title);
            }
            if (this.subtitle) {
                const subtitle = document.createElement('p');
                subtitle.className = 'no-modal__subtitle no-body-lg no-mg-sm--t';
                subtitle.textContent = this.subtitle;
                titleGroup.appendChild(subtitle);
            }
            titleContainer.appendChild(titleGroup);
        }

        header.appendChild(titleContainer);

        // 닫기 버튼
        const closeBtn = document.createElement('button');
        closeBtn.className = 'no-modal__close';
        closeBtn.setAttribute('aria-label', '닫기');
        closeBtn.innerHTML = '<i class="fa-regular fa-xmark"></i>';
        closeBtn.addEventListener('click', () => this.close());
        header.appendChild(closeBtn);

        // 모달 바디
        const body = document.createElement('div');
        body.className = 'no-modal__body';

        // 컨텐츠 렌더링
        if (this.content instanceof ModalContent) {
            body.innerHTML = this.content.render();
        } else if (this.content) {
            body.innerHTML = this.content;
        } else if (this.message) {
            const messageElement = document.createElement('p');
            messageElement.className = 'no-modal__message';
            messageElement.textContent = this.message;
            body.appendChild(messageElement);
        }

        // 확인 버튼 (기본)
        if (!this.content || (this.content instanceof ModalContent && this.content.buttons.length === 0)) {
            const actions = document.createElement('div');
            actions.className = 'no-modal__actions';
            const confirmBtn = document.createElement('button');
            confirmBtn.className = 'no-btn no-btn--main';
            confirmBtn.textContent = '확인';
            confirmBtn.addEventListener('click', () => {
                if (this.onConfirm) {
                    this.onConfirm();
                }
                this.close();
            });
            actions.appendChild(confirmBtn);
            body.appendChild(actions);
        } else if (this.content instanceof ModalContent && this.content.buttons.length > 0) {
            // ModalContent의 버튼 이벤트 처리
            body.querySelectorAll('[data-action]').forEach(btn => {
                const action = btn.getAttribute('data-action');
                btn.addEventListener('click', () => {
                    if (action === 'confirm' && this.onConfirm) {
                        this.onConfirm();
                    }
                    this.close();
                });
            });
        }

        // 모달 조립
        this.modalElement.appendChild(header);
        this.modalElement.appendChild(body);
        this.overlayElement.appendChild(this.modalElement);

        // 이벤트 리스너
        this.attachEvents();

        return this;
    }

    /**
     * 타입별 아이콘 반환
     */
    getIcon() {
        const icons = {
            alert: '<i class="fa-regular fa-circle-exclamation"></i>',
            info: '<i class="fa-regular fa-circle-info"></i>',
            error: '<i class="fa-regular fa-circle-xmark"></i>',
            success: '<i class="fa-regular fa-circle-check"></i>',
        };
        return icons[this.type] || null;
    }

    /**
     * 이벤트 리스너 연결
     */
    attachEvents() {
        // 오버레이 클릭으로 닫기
        this.overlayElement.addEventListener('click', (e) => {
            if (e.target === this.overlayElement) {
                this.close();
            }
        });

        // ESC 키로 닫기
        const handleEscape = (e) => {
            if (e.key === 'Escape' && this.isOpen) {
                this.close();
                document.removeEventListener('keydown', handleEscape);
            }
        };
        document.addEventListener('keydown', handleEscape);
    }

    /**
     * 모달 열기
     */
    open() {
        if (this.isOpen) return;

        if (!this.modalElement) {
            this.create();
        }

        document.body.appendChild(this.overlayElement);
        document.body.style.overflow = 'hidden';

        // 애니메이션을 위한 약간의 지연
        setTimeout(() => {
            this.overlayElement.classList.add('is-open');
            this.modalElement.classList.add('is-open');
        }, 10);

        this.isOpen = true;

        // 자동 닫기
        if (this.autoClose) {
            this.autoCloseTimer = setTimeout(() => {
                this.close();
            }, this.autoCloseDelay);
        }
    }

    /**
     * 모달 닫기
     */
    close() {
        if (!this.isOpen) return;

        if (this.autoCloseTimer) {
            clearTimeout(this.autoCloseTimer);
            this.autoCloseTimer = null;
        }

        this.overlayElement.classList.remove('is-open');
        this.modalElement.classList.remove('is-open');

        setTimeout(() => {
            if (this.overlayElement && this.overlayElement.parentNode) {
                this.overlayElement.parentNode.removeChild(this.overlayElement);
            }
            document.body.style.overflow = '';
            this.isOpen = false;

            if (this.onClose) {
                this.onClose();
            }
        }, 300);
    }

    /**
     * 모달 제거
     */
    destroy() {
        this.close();
        this.modalElement = null;
        this.overlayElement = null;
    }

    /**
     * 정적 메서드: 간단한 alert 모달
     */
    static alert(message, title = '알림', options = {}) {
        const modal = new Modal({
            type: 'alert',
            message,
            title,
            depth: Modal.DEPTH.LOW,
            ...options
        });
        modal.open();
        return modal;
    }

    /**
     * 정적 메서드: info 모달
     */
    static info(message, title = '안내', options = {}) {
        const modal = new Modal({
            type: 'info',
            message,
            title,
            depth: Modal.DEPTH.LOW,
            ...options
        });
        modal.open();
        return modal;
    }

    /**
     * 정적 메서드: error 모달
     */
    static error(message, title = '오류', options = {}) {
        const modal = new Modal({
            type: 'error',
            message,
            title,
            depth: Modal.DEPTH.HIGH, // 에러는 높은 우선순위
            ...options
        });
        modal.open();
        return modal;
    }

    /**
     * 정적 메서드: success 모달
     */
    static success(message, title = '성공', options = {}) {
        const modal = new Modal({
            type: 'success',
            message,
            title,
            depth: Modal.DEPTH.MEDIUM, // 성공은 중간 우선순위
            autoClose: true,
            autoCloseDelay: 3000,
            ...options
        });
        modal.open();
        return modal;
    }

    /**
     * 정적 메서드: 커스텀 컨텐츠 모달
     */
    static custom(content, type = 'alert', options = {}) {
        const modal = new Modal({
            type,
            content,
            ...options
        });
        modal.open();
        return modal;
    }
}

